import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Total Composites';
const BASE_URL = 'https://totalcomposites.com';
const DEFAULT_IMAGE = 'https://totalcomposites.com/wp-content/uploads/2024/05/Logo-TC-White-scaled.png';

export default function SEO({ title, description, path = '/', image, type = 'website', keywords, jsonLd }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Expedition Truck Campers & Composite Systems`;
  const url = `${BASE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Geo Tags */}
      <meta name="geo.region" content="CA-BC" />
      <meta name="geo.placename" content="Victoria, British Columbia" />

      {/* JSON-LD Structured Data */}
      {jsonLd && !Array.isArray(jsonLd) && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      {jsonLd && Array.isArray(jsonLd) && jsonLd.map((schema, i) => (
        <script key={schema?.["@type"] || `schema-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

/* ── Reusable Schema Builders ─────────────────────────────────────────────── */

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Total Composites',
  url: 'https://www.totalcomposites.com',
  logo: 'https://www.totalcomposites.com/logo.png',
  image: 'https://www.totalcomposites.com/og-image.jpg',
  description: 'Total Composites designs and manufactures premium 4-season composite truck camper shells and expedition truck body kits. Serving North America and worldwide since 2017.',
  founder: { '@type': 'Person', name: 'Andreas Schwall' },
  foundingDate: '2017',
  address: { '@type': 'PostalAddress', addressLocality: 'Victoria', addressRegion: 'BC', addressCountry: 'CA' },
  geo: { '@type': 'GeoCoordinates', latitude: 48.4284, longitude: -123.3656 },
  areaServed: ['CA', 'US', 'worldwide'],
  telephone: '+1-xxx-xxx-xxxx',
  priceRange: '$$$',
  sameAs: [
    'https://www.facebook.com/totalcomposites',
    'https://www.instagram.com/totalcomposites',
    'https://www.youtube.com/totalcomposites'
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Truck Camper Shells & Expedition Kits',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Lynx Slide-In Camper Shell' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Wolf Slide-In Camper Shell' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Bobcat Flatbed Camper Shell' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Wolverine Flatbed Camper Shell' } }
    ]
  }
};

// Keep for backward compat
export const organizationSchema = localBusinessSchema;
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Total Composites',
  url: 'https://www.totalcomposites.com',
  description: 'Premium 4-season composite truck camper shells. Zero condensation. Ships worldwide.',
  publisher: { '@type': 'Organization', name: 'Total Composites' }
};

export function productSchema({ name, description, sku, category, material, weight, price, currency = 'USD', url, ratingValue, reviewCount }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    sku,
    brand: { '@type': 'Brand', name: 'Total Composites' },
    url,
    offers: {
      '@type': 'Offer',
      priceCurrency: currency,
      price,
      availability: 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: 'Total Composites' }
    }
  };
  if (category) schema.category = category;
  if (material) schema.material = material;
  if (weight) schema.weight = weight;
  if (ratingValue && reviewCount) {
    schema.aggregateRating = { '@type': 'AggregateRating', ratingValue, reviewCount };
  }
  return schema;
}

export function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer
      }
    }))
  };
}

export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${BASE_URL}${item.path}`
    }))
  };
}
